import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import InstagramFeed, { InstagramStrip } from '../components/InstagramFeed';
import instagramFeed from '../data/instagram.json';
import events from '../data/events';
import {
  PlayIcon,
  CalendarIcon,
  PencilIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

// 2026-2027 school year calendar (Google Sheets)
const YEARLY_CALENDAR_URL = 'https://docs.google.com/spreadsheets/d/1V3v3BPSEZBYuv855d39n-Yq3x-J5Xlqt/edit?usp=drive_link&ouid=116348819300951825311&rtpof=true&sd=true';

// Flyers shown under "Event Flyers" on the homepage. Empty hides the section.
// Entry shape: { src, alt, lang? } (lang only when the flyer is not English).
const eventFlyers = [];

const septemberFlyer = {
  src: '/assets/images/flyers/September_events.jpg',
  alt: 'November/December Attendance Challenge',
};

// Homepage calendar: the next stretch of src/data/events.js as a dated timeline,
// so it moves on by itself (an event drops off the day after it ends).
const HOME_EVENT_COUNT = 8;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const longDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

// True when every day strictly between a and b is a weekend, so a recess
// stored Thu-Fri and Mon-Fri still reads as one stretch.
const onlyWeekendBetween = (a, b) => {
  const d = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1);
  while (d < b) {
    if (d.getDay() !== 0 && d.getDay() !== 6) return false;
    d.setDate(d.getDate() + 1);
  }
  return true;
};

const homeNote = (e, first, last) => {
  if (last > first) return `${longDate(first)} through ${longDate(last)}`;
  if (e.description) return e.description;
  const time = e.allDay ? '' : [e.startTime, e.endTime].filter(Boolean).join(' – ');
  return [time, e.location].filter(Boolean).join(' · ');
};

// One row per event in date order; multi-day events (stored one entry per day)
// fold into a single row.
const HOME_EVENTS = [...events]
  .sort((a, b) => a.date - b.date)
  .reduce((rows, e) => {
    const prev = rows[rows.length - 1];
    if (prev && prev.event.title === e.title && e.date > prev.last && onlyWeekendBetween(prev.last, e.date)) {
      prev.last = e.date;
      return rows;
    }
    rows.push({ event: e, first: e.date, last: e.date });
    return rows;
  }, [])
  .map(({ event, first, last }) => ({
    id: event.id,
    mo: MONTHS[first.getMonth()],
    day: String(first.getDate()),
    title: event.title,
    note: homeNote(event, first, last),
    lastKey: dayKey(last),
  }));

// The pre-rendered HTML is built with the build date (REACT_APP_BUILD_DATE, set
// by the build script); after hydration the visitor's own date takes over, so
// an old build never shows events that have already passed.
const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || '0000-00-00';
const upcomingEvents = (today) =>
  HOME_EVENTS.filter((e) => e.lastKey >= today).slice(0, HOME_EVENT_COUNT);

const Home = () => {
  const [todayKey, setTodayKey] = useState(BUILD_DATE);
  useEffect(() => setTodayKey(dayKey(new Date())), []);
  const upcoming = upcomingEvents(todayKey);
  // track which animated sections are visible on scroll (mutable ref to avoid unused state warning)
  const isVisibleRef = useRef({});
  // lazy-load and buffering state for monthly news video
  const [loadNewsVideo, setLoadNewsVideo] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('may');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [playPromo, setPlayPromo] = useState(false);
  const newsVideoRef = useRef(null);
  const newsContainerRef = useRef(null);

  // Slideshow state for gallery images
  const [currentSlide, setCurrentSlide] = useState(0);
  const galleryImages = [
    '/assets/images/homepage/assemblyman_angelo_1.jpeg',
    '/assets/images/homepage/assemblyman_angelo.JPG',
    '/assets/images/homepage/Family_Dinner_1.jpeg',
    '/assets/images/homepage/lincoln_2.jpeg',
    '/assets/images/homepage/heart_heroes.jpeg',
  ];

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('[data-animate]');
      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInView) {
          isVisibleRef.current = { ...isVisibleRef.current, [index]: true };
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // IntersectionObserver: start loading the news video when the News & Events section scrolls into view
  useEffect(() => {
    if (!newsContainerRef.current) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setLoadNewsVideo(true);
          obs.disconnect();
        }
      });
    }, { threshold: 0.25 });

    observer.observe(newsContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance slideshow every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [galleryImages.length]);

  return (
    <>
      <Navbar />
      <main id="main-content" className="bg-white overflow-hidden">

      {/* Hero Section. The video and its overlay are absolutely positioned, so
          they don't register as a background for the text via the DOM ancestor
          chain. The section itself carries a real, solid navy background-color
          so the white hero text always has a genuine, computable, high-contrast
          backdrop — including before the video has loaded. */}
      <section className="relative min-h-screen max-h-screen overflow-hidden bg-brand-navy">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-label="PSMS 108 School of Authors campus video"
            className="w-full h-full object-cover"
          >
            <source src="/assets/videos/home/ps_108.mp4" type="video/mp4" />
            <div className="absolute inset-0 bg-brand-primary"></div>
          </video>
          {/* Light navy wash: keeps the campus footage bright while the text
              shadow below keeps the white type readable over it. */}
          <div className="absolute inset-0 bg-brand-navy/25"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/45 via-transparent to-brand-navy/50"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 min-h-screen max-h-screen flex items-center justify-center px-4 py-24 md:py-32 pb-40 md:pb-32 lg:pb-0 [text-shadow:0_2px_14px_rgba(15,23,42,0.55)]">
          <div className="content-container">
            <div className="max-w-5xl mx-auto text-center">
              {/* Badge */}
              <div className="mb-6 md:mb-12">
                <span className="inline-block bg-black/30 backdrop-blur-sm border border-white/20 text-white px-4 md:px-8 py-2 md:py-3 text-xs md:text-sm font-medium tracking-wider">
                  Great Things Happen at 108
                </span>
              </div>

              {/* Main Heading */}
              <div className="mb-8 md:mb-12">
                <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-hero font-display text-white mb-4 md:mb-8 leading-none tracking-tight">
                  PSMS 108
                </h1>
                <div className="flex items-center justify-center space-x-4 md:space-x-8 mb-6 md:mb-10">
                  <div className="h-px bg-gradient-to-r from-brand-accent via-brand-yellow to-brand-navy w-12 md:w-24"></div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-display text-white/95 font-light tracking-wide whitespace-nowrap">
                    School of Authors
                  </h2>
                  <div className="h-px bg-gradient-to-l from-brand-accent via-brand-yellow to-brand-navy w-12 md:w-24"></div>
                </div>
              </div>

              {/* Tagline */}
              <div className="mb-12 md:mb-20">
                <h3 className="text-xl md:text-2xl lg:text-title text-brand-accent font-display mb-4 md:mb-8">
                  Where Students are Writing Their Futures
                </h3>
                <p className="text-sm md:text-base lg:text-xl text-white/90 leading-relaxed max-w-4xl mx-auto font-body px-4">
                  Where students use their skills and agency to create their own futures and strive for peace,
                  justice, and dignity for all people. Serving East Harlem's 3K-8 community for over 30 years.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Bottom Right (Desktop) / Bottom Center (Mobile) */}
        <div className="absolute bottom-8 lg:bottom-12 left-1/2 lg:left-auto lg:right-12 transform -translate-x-1/2 lg:translate-x-0 z-20">
          <div className="flex flex-col gap-4 w-64 lg:w-auto">
            <a href="https://www.instagram.com/schoolofauthors/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center lg:justify-start bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium px-6 lg:px-8 py-3 lg:py-4 hover:bg-white/20 transition-all duration-300 group">
              <PlayIcon className="inline-block mr-2 lg:mr-3 h-4 lg:h-5 w-4 lg:w-5" />
              Check Us Out On Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Monthly flyer ("October Events"): the second thing visitors see, right after the hero. */}
      <section className="section-padding bg-white relative overflow-hidden" data-animate>
        <div className="content-container relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-brand-accent"></div>
              <span className="text-brand-accent-dark font-medium tracking-wide">Mark Your Calendar</span>
              <div className="w-8 h-px bg-brand-accent"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-4">October Events</h2>
            <p className="text-lg text-body max-w-3xl mx-auto">Everything happening at 108 School of Authors this month.</p>
          </div>

          <div className="max-w-md mx-auto">
            <div
              className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-accent/30 hover:-translate-y-2 cursor-pointer"
              onClick={() => setEnlargedImage(septemberFlyer)}
              role="button"
              aria-label={`Expand: ${septemberFlyer.alt}`}
            >
              <img src={septemberFlyer.src} alt={septemberFlyer.alt} className="w-full h-auto object-contain" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-lg">
                  <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Flyers. Hidden while there are none; add { src, alt } entries to eventFlyers (top of file) to show it. */}
      {eventFlyers.length > 0 && (
        <section className="section-padding bg-white relative overflow-hidden" data-animate>
          <div className="content-container relative z-10">
            <div className="text-center mb-10">
              <h2 className="text-display text-heading font-display">Event Flyers</h2>
              <div className="flex items-center justify-center space-x-3 mt-3">
                <div className="w-8 h-px bg-brand-navy/40"></div>
                <div className="w-2 h-2 bg-brand-yellow rounded-full"></div>
                <div className="w-8 h-px bg-brand-navy/40"></div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-8 max-w-5xl mx-auto">
              {eventFlyers.map((flyer, i) => (
                <div
                  key={i}
                  className="group relative w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)] bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-accent/30 hover:-translate-y-2 cursor-pointer"
                  onClick={() => setEnlargedImage(flyer)}
                  role="button"
                  lang={flyer.lang}
                  aria-label={`${flyer.lang === 'es' ? 'Ampliar' : 'Expand'}: ${flyer.alt}`}
                >
                  <img src={flyer.src} alt={flyer.alt} lang={flyer.lang} className="w-full h-auto object-contain" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-lg">
                      <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Yearly Calendar Section */}
      <section className="section-padding bg-blue-50 relative overflow-hidden" data-animate>
        {/* Background decorative elements */}
        <div aria-hidden="true" className="opacity-5">
          <div className="absolute top-10 right-10 w-32 h-32 border-2 border-brand-navy rounded-full"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-brand-yellow transform rotate-12"></div>
        </div>

        <div className="content-container relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-gradient-to-r from-brand-navy to-brand-yellow"></div>
              <CalendarIcon className="h-6 w-6 text-brand-accent" />
              <div className="w-8 h-px bg-gradient-to-l from-brand-navy to-brand-yellow"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-4">Yearly Calendar</h2>
            <p className="text-lg text-body max-w-3xl mx-auto">Here's what's coming up next at 108 School of Authors — view the full 2026-2027 school year calendar below.</p>
          </div>

          {upcoming.length === 0 ? (
            <p className="text-center text-lg text-body">See the full calendar for what's coming up.</p>
          ) : (
            <ol className="grid gap-5 lg:grid-cols-2 lg:gap-x-12 max-w-6xl mx-auto">
              {upcoming.map((e, i) => (
                <li key={e.id} className="relative pl-[4.5rem] sm:pl-20">
                  {/* Dashed rail runs through the middle of the date chip. */}
                  <span aria-hidden="true" className="absolute inset-y-0 left-7 border-l-2 border-dashed border-brand-navy/40" />
                  <span
                    className={`absolute left-0 top-0 grid h-14 w-14 place-items-center rounded-xl border-[3px] ${
                      i % 2 === 0 ? 'border-brand-navy' : 'border-brand-yellow'
                    } bg-white text-brand-navy shadow-soft`}
                  >
                    <span className="text-center leading-none">
                      <span className="block text-[0.62rem] font-bold uppercase tracking-wide text-brand-text-muted">{e.mo}</span>
                      <span className="block text-xl font-bold">{e.day}</span>
                    </span>
                  </span>
                  <div className="h-full rounded-2xl border-2 border-brand-navy bg-white p-5 shadow-soft">
                    <h3 className="text-lg font-semibold leading-tight text-brand-navy">{e.title}</h3>
                    {e.note && <p className="mt-1.5 text-body">{e.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={YEARLY_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center btn-primary"
            >
              View 2026-2027 Yearly Calendar
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="sr-only">(opens in a new tab)</span>
            </a>
            <a href="/calendar" className="inline-flex items-center text-brand-primary font-medium hover:underline">
              Browse the event calendar
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Promotional Film */}
      <section className="section-padding bg-white relative overflow-hidden" data-animate>
        {/* Background decorative elements */}
        <div aria-hidden="true" className="opacity-5 pointer-events-none">
          <div className="absolute top-16 left-12 w-40 h-40 border-2 border-brand-navy transform rotate-45"></div>
          <div className="absolute bottom-16 right-16 w-28 h-28 bg-brand-yellow transform rotate-12"></div>
        </div>

        <div className="content-container relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-brand-accent"></div>
              <PlayIcon className="h-6 w-6 text-brand-accent" />
              <div className="w-8 h-px bg-brand-accent"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-4">Experience 108</h2>
            <p className="text-lg text-body max-w-3xl mx-auto">
              Step inside the School of Authors and see what makes our community great.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative aspect-video bg-brand-light rounded-xl overflow-hidden border-2 border-brand-navy/10 shadow-elegant">
              {/* Thumbnail first: a still from the film with a play button. The
                  video itself (24 MB) only loads once someone chooses to watch. */}
              {playPromo ? (
                <video
                  controls
                  autoPlay
                  playsInline
                  poster="/assets/images/home/soa_promo_film_poster.jpg"
                  aria-label="PSMS 108 School of Authors promotional film"
                  className="absolute inset-0 w-full h-full bg-black"
                >
                  <source src="/assets/videos/home/soa_promo_film.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <button
                  type="button"
                  onClick={() => setPlayPromo(true)}
                  className="group absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-yellow"
                >
                  <img
                    src="/assets/images/home/soa_promo_film_poster.jpg"
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none"
                  />
                  <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-brand-navy/50 via-transparent to-transparent" />
                  <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 shadow-xl transition-transform duration-300 group-hover:scale-110 motion-reduce:transition-none">
                      <PlayIcon className="w-9 h-9 md:w-11 md:h-11 text-brand-primary translate-x-0.5" />
                    </span>
                  </span>
                  <span aria-hidden="true" className="absolute bottom-4 left-4 md:bottom-6 md:left-6 bg-white text-brand-navy text-sm font-medium px-3 py-1.5 rounded-full shadow">
                    Watch the film · 5:44
                  </span>
                  <span className="sr-only">Play the Experience 108 film, 5 minutes 44 seconds</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Community Announcement */}
      <section className="section-padding bg-blue-50 relative overflow-hidden" data-animate>
        {/* Creative geometric shapes */}
        <div aria-hidden="true" className="opacity-5">
          <div className="absolute top-20 left-10 w-32 h-32 border-2 border-brand-navy transform rotate-45"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-brand-yellow transform rotate-12"></div>
          <div className="absolute top-1/2 right-10 w-16 h-16 border-2 border-brand-navy rounded-full"></div>
        </div>

        <div className="content-container relative z-10">
          <div className="text-center mb-12">
            <div className="relative">
              <span className="text-brand-accent-dark font-medium mb-4 block tracking-wide">Community Support</span>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-brand-yellow rounded-full animate-pulse"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-6">Great Things Happen At 108!</h2>
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-px bg-gradient-to-r from-brand-navy to-brand-yellow"></div>
              <div className="w-3 h-3 border-2 border-brand-yellow rounded-full"></div>
              <div className="w-16 h-px bg-gradient-to-l from-brand-navy to-brand-yellow"></div>
            </div>
          </div>

          {/* Gallery Slideshow */}
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-xl shadow-lg bg-white border-2 border-brand-navy/10">
              <div className="relative aspect-[16/9]">
                {galleryImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image}
                      alt={['Assemblyman Angelo with PSMS 108 students', 'Assemblyman Angelo visit to PSMS 108', 'PSMS 108 Family Dinner event', 'Lincoln event at PSMS 108', 'Heart Heroes'][index]}
                      className="w-full h-full object-contain bg-blue-50"
                    />
                  </div>
                ))}
                {/* Gradient overlay for better visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {/* Slide indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? 'bg-brand-yellow w-6'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram, as on PS 96: the photo grid once scripts/sync-instagram.mjs has
          synced posts (setup in INSTAGRAM_FEED.md), the gold follow strip until then. */}
      {instagramFeed.posts.length >= 3 ? (
        <InstagramFeed feed={instagramFeed} />
      ) : (
        <InstagramStrip feed={instagramFeed} facebookUrl="https://www.facebook.com/108soa/" />
      )}

      {/* Social Media - Prominent Section */}
      <section className="section-padding bg-brand-primary/5" data-animate>
        <div className="content-container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-brand-accent"></div>
              <span className="text-brand-accent-dark font-medium tracking-wide">Connect With Us</span>
              <div className="w-8 h-px bg-brand-accent"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-4">Follow Our Community</h2>
            <p className="text-body max-w-2xl mx-auto">Stay up to date with stories, events, and student highlights.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/108soa/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-gray-100 hover:border-brand-accent/30 p-8 flex items-center rounded-lg hover:shadow-elegant transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
            >
              <div className="relative w-16 h-16 mr-6 rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                <img src="/assets/images/logo.png" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-white/40"></div>
                <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#1877F2] rounded-tl-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M22 12.06C22 6.48 17.52 2 11.94 2S2 6.48 2 12.06c0 4.98 3.66 9.12 8.44 9.94v-7.03H7.9V12.06h2.54V9.84c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.91h-2.34V22c4.78-.82 8.44-4.96 8.44-9.94z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-heading font-medium text-lg group-hover:text-brand-primary transition-colors">Facebook</div>
                <div className="text-sm text-brand-text-muted">News, updates, and community highlights</div>
              </div>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/schoolofauthors/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-gray-100 hover:border-brand-accent/30 p-8 flex items-center rounded-lg hover:shadow-elegant transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
            >
              <div className="relative w-16 h-16 mr-6 rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                <img src="/assets/images/logo.png" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-white/40"></div>
                <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#E1306C] rounded-tl-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm0 2.2a2.8 2.8 0 100 5.6 2.8 2.8 0 000-5.6zM18 6.5a1 1 0 110 2 1 1 0 010-2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-heading font-medium text-lg group-hover:text-brand-primary transition-colors">Instagram</div>
                <div className="text-sm text-brand-text-muted">Student work and campus life</div>
              </div>
            </a>

            {/* PTA Instagram */}
            <a
              href="https://www.instagram.com/108pta?stkn=MTl3aWU5ZXM5OWRhYw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-gray-100 hover:border-brand-accent/30 p-8 flex items-center rounded-lg hover:shadow-elegant transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
            >
              <div className="relative w-16 h-16 mr-6 rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                <img src="/assets/images/logo.png" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-white/40"></div>
                <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#E1306C] rounded-tl-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm0 2.2a2.8 2.8 0 100 5.6 2.8 2.8 0 000-5.6zM18 6.5a1 1 0 110 2 1 1 0 010-2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-heading font-medium text-lg group-hover:text-brand-primary transition-colors">PTA Instagram</div>
                <div className="text-sm text-brand-text-muted">Updates and events from our PTA</div>
              </div>
            </a>

            {/* YouTube */}
            <a
              href="https://www.youtube.com/channel/UCwsJmvWoUxdvLPyYOhBWCvg"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-gray-100 hover:border-brand-accent/30 p-8 flex items-center rounded-lg hover:shadow-elegant transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
            >
              <div className="relative w-16 h-16 mr-6 rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                <img src="/assets/images/logo.png" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-white/40"></div>
                <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#FF0000] rounded-tl-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.8 31.8 0 000 12a31.8 31.8 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.8 31.8 0 0024 12a31.8 31.8 0 00-.5-5.8zM9.8 15.5V8.5L15.5 12l-5.7 3.5z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-heading font-medium text-lg group-hover:text-brand-primary transition-colors">YouTube</div>
                <div className="text-sm text-brand-text-muted">Performances, projects, and highlights</div>
              </div>
            </a>

            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@psms108"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-gray-100 hover:border-brand-accent/30 p-8 flex items-center rounded-lg hover:shadow-elegant transition-all duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
            >
              <div className="relative w-16 h-16 mr-6 rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                <img src="/assets/images/logo.png" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-white/40"></div>
                <div className="absolute bottom-0 right-0 w-7 h-7 bg-black rounded-tl-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.48a8.18 8.18 0 004.76 1.52V7.56a4.84 4.84 0 01-1-.87z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-heading font-medium text-lg group-hover:text-brand-primary transition-colors">TikTok</div>
                <div className="text-sm text-brand-text-muted">Short videos and school moments</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="section-padding bg-brand-light relative overflow-hidden" data-animate>
        {/* Creative background pattern */}
        <div aria-hidden="true" className="opacity-5">
          <div className="absolute top-10 left-10 w-40 h-40 border border-brand-navy rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-60 h-60 border border-brand-yellow rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-brand-navy/10 rounded-full"></div>
        </div>

        <div className="content-container relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-gradient-to-r from-brand-navy to-brand-yellow"></div>
              <span className="text-brand-accent-dark font-medium tracking-wide">Quick Access</span>
              <div className="w-8 h-px bg-gradient-to-l from-brand-navy to-brand-yellow"></div>
            </div>
            <h2 className="text-display text-heading font-display mb-6">Everything You Need</h2>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-4 h-px bg-brand-navy/50"></div>
              <div className="w-2 h-2 bg-brand-yellow rounded-full"></div>
              <div className="w-4 h-px bg-brand-navy/50"></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8">
            {[
              { title: "Breakfast Menu", href: "https://www.schools.nyc.gov/school-life/food/menus/breakfast-meals?value=s1", iconImg: '/assets/images/lunch_menu.png', external: true },
              { title: "Lunch Menu", href: "https://www.schools.nyc.gov/school-life/food/menus/school-lunch-meals?value=s8", iconImg: '/assets/images/lunch_menu.png', external: true },
              { title: "Calendar", href: "/calendar", icon: CalendarIcon },
              { title: "JupiterGrades", href: "https://login.jupitered.com/login/", iconImg: '/assets/images/Jupiter.jpeg', external: true },
              { title: "NYC Schools Account", href: "https://www.schools.nyc.gov/learning/student-journey/nyc-schools-account", iconImg: '/assets/images/NYC_Account.png', external: true },
              { title: "My Schools", href: "https://myschools.nyc", iconImg: '/assets/images/my_schools.jpeg', external: true },
              { title: "NYCDOE Home Page", href: "https://schools.nyc.gov", iconImg: '/assets/images/nyc_schools.jpeg', external: true },
            ].map((item, index) => (
              <a
                key={index}
                href={item.href}
                target={item.external ? "_blank" : "_self"}
                rel={item.external ? "noopener noreferrer" : ""}
                className="group relative bg-white p-8 text-center hover:shadow-elegant transition-all duration-300 border border-gray-100 hover:border-brand-navy/30 hover:-translate-y-1"
              >
                <div className="relative">
                  <div className="w-16 h-16 mx-auto mb-6 bg-blue-50 rounded-2xl flex items-center justify-center group-hover:bg-brand-yellow/10 transition-all duration-300 border border-blue-100 group-hover:border-brand-navy/20 overflow-hidden">
                    {item.iconImg ? (
                      <img src={item.iconImg} alt="" className="w-full h-full object-contain p-1" />
                    ) : (
                      <item.icon className="w-8 h-8 text-brand-text-muted group-hover:text-brand-navy transition-colors duration-300" />
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-heading group-hover:text-brand-primary transition-colors leading-tight">
                    {item.title}
                  </h3>
                </div>

                {/* Subtle accent line on hover */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-navy via-brand-yellow to-brand-navy group-hover:w-12 transition-all duration-300"></div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly News Video directly under Quick Access */}
      <section className="section-padding bg-white" data-animate>
        <div className="content-container" ref={newsContainerRef}>
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-brand-accent"></div>
              <span className="text-brand-accent-dark font-medium tracking-wide">Student News</span>
              <div className="w-8 h-px bg-brand-accent"></div>
            </div>
            <h2 className="text-display text-heading font-display">SOA News</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">Watch the latest news and updates from our School of Authors</p>
          </div>

          {/* Month selector tabs */}
          <div className="flex justify-center mb-8">
            {/* flex-wrap: as one unwrapped row this was ~700px wide and centred, so on a phone the
                first and last buttons were clipped off both edges and could not be scrolled to. */}
            <div className="flex flex-wrap justify-center gap-1 bg-blue-50 rounded-lg p-1 border border-blue-200">
              {[
                { id: 'may', label: 'May 2026' },
                { id: 'civics', label: 'Civics For All 2026' },
                { id: 'february', label: 'February 2026' },
                { id: 'january', label: 'January 2026' },
                { id: 'december', label: 'December 2025' },
                { id: 'november', label: 'November 2025' }
              ].map((month) => (
                <button
                  key={month.id}
                  onClick={() => {
                    setSelectedMonth(month.id);
                    setLoadNewsVideo(true);
                  }}
                  aria-pressed={selectedMonth === month.id}
                  className={`px-4 sm:px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    selectedMonth === month.id
                      ? 'bg-brand-primary text-white shadow-md'
                      : 'text-brand-text-secondary hover:bg-white hover:text-brand-primary'
                  }`}
                >
                  {month.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg overflow-hidden border border-blue-200 max-w-5xl mx-auto">
            {loadNewsVideo ? (
              <div className="relative">
                <video
                  key={selectedMonth}
                  ref={newsVideoRef}
                  controls
                  preload="metadata"
                  className="w-full h-auto"
                  onWaiting={() => setIsBuffering(true)}
                  onPlaying={() => setIsBuffering(false)}
                  onCanPlay={() => setIsBuffering(false)}
                >
                  <source
                    src={
                      selectedMonth === 'may'
                        ? '/assets/videos/home/soa_news_may.mp4'
                        : selectedMonth === 'civics'
                        ? '/assets/videos/home/civics_for_all_2026.mp4'
                        : selectedMonth === 'february'
                        ? '/assets/videos/home/soa_news_february.mp4'
                        : selectedMonth === 'january'
                        ? '/assets/videos/home/soa_news_january.mp4'
                        : selectedMonth === 'december'
                        ? '/assets/videos/home/soa_news_december.mp4'
                        : '/assets/videos/home/soa_news_first_day.mp4'
                    }
                    type="video/mp4"
                  />
                  <track kind="captions" src="/assets/videos/home/soa_news_first_day.vtt" srcLang="en" label="English" />
                  Your browser does not support the video tag.
                </video>
                {isBuffering && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <div className="loader">Loading…</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-48 flex items-center justify-center bg-blue-50">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setLoadNewsVideo(true)}
                >
                  Load SOA News Videos
                </button>
              </div>
            )}
          </div>

          {/* Note for videos */}
          <div className="mt-6 text-center">
            <p className="text-sm text-brand-text-muted italic">
              Click on the tabs above to view episodes from different months. <strong>Latest:</strong> May 2026 · <strong>Special Episode:</strong> SOA News presents Civics For All — Citywide Kickoff.
            </p>
          </div>
        </div>
      </section>

      {/* Student Highlights */}
      <section className="section-padding bg-white relative overflow-hidden" data-animate>
        {/* Background decorative elements */}
        <div aria-hidden="true" className="opacity-5 pointer-events-none">
          <div className="absolute top-20 right-20 w-40 h-40 border border-brand-accent rounded-full"></div>
          <div className="absolute bottom-20 left-10 w-32 h-32 bg-brand-yellow/20 transform rotate-12"></div>
        </div>

        <div className="content-container relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-8 h-px bg-brand-accent"></div>
              <AcademicCapIcon className="h-6 w-6 text-brand-accent" />
              <div className="w-8 h-px bg-brand-accent"></div>
            </div>
            <h2 className="text-display text-heading font-display">Student Highlights</h2>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white relative" data-animate>
        {/* Creative quote element */}
        <div aria-hidden="true" className="absolute top-20 right-10 opacity-10 pointer-events-none">
          <div className="text-9xl font-display text-brand-navy">"</div>
        </div>

        <div className="content-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

            {/* Left: Content */}
            <div>
              <span className="text-brand-accent-dark font-medium mb-8 block tracking-wide">Student Voices</span>

              <h2 className="text-display text-heading font-display mb-8 leading-tight">
                What Are Students Saying?
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-lg text-body">
                  We serve grades 3K through 8 and nurture all students' social and emotional growth through
                  thoughtful consideration of each individual student's strengths and needs, engaging curriculum and high expectations for all.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-12">
                <div className="text-center p-8 bg-brand-navy/5 border border-brand-navy/20">
                  <div className="text-4xl font-display text-brand-navy mb-2">95%</div>
                  <div className="text-sm text-muted uppercase tracking-wide">Positive Cultural Representation</div>
                  <div className="w-12 h-px bg-brand-navy mx-auto mt-4"></div>
                </div>
                <div className="text-center p-8 bg-brand-yellow/5 border border-brand-yellow/20">
                  <div className="text-4xl font-display text-brand-primary mb-2">94%</div>
                  <div className="text-sm text-muted uppercase tracking-wide">Culturally Responsive Lessons</div>
                  <div className="w-12 h-px bg-brand-yellow mx-auto mt-4"></div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-body">
                  <strong>We have open enrollment.</strong> Schedule a visit today to learn more about our community of authors.
                </p>
                <a href="/contact" className="inline-flex items-center btn-primary">Schedule a Visit</a>
              </div>
            </div>

            {/* Right: Visual Elements */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-8 h-[600px]">
                <div className="space-y-8">
                  <div className="bg-blue-50 h-48 p-8 flex flex-col justify-end border-l-4 border-brand-accent">
                    <h3 className="font-display text-heading mb-2">Student Safety</h3>
                    <p className="text-sm text-muted">Feel safe in classes</p>
                    <div className="mt-4 w-8 h-8 bg-brand-accent/20 rounded-full flex items-center justify-center">
                      <PencilIcon className="w-4 h-4 text-brand-accent" />
                    </div>
                  </div>
                  <div className="bg-brand-accent/5 h-32 p-6 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="font-display text-heading text-2xl mb-1">92%</h3>
                      <p className="text-xs text-muted uppercase tracking-wide">Feel Safe</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-8 pt-16">
                  <div className="bg-brand-primary/5 h-32 p-6 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="font-display text-heading text-2xl mb-1">91%</h3>
                      <p className="text-xs text-muted uppercase tracking-wide">Voice is Heard</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 h-48 p-8 flex flex-col justify-end border-l-4 border-brand-primary">
                    <h3 className="font-display text-heading mb-2">Teacher Openness</h3>
                    <p className="text-sm text-muted">Open to student ideas</p>
                    <div className="mt-4 w-8 h-8 bg-brand-primary/20 rounded-full flex items-center justify-center">
                      <AcademicCapIcon className="w-4 h-4 text-brand-primary" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating accent element */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white shadow-lg rounded-full flex items-center justify-center z-10">
                <div className="w-8 h-8 bg-brand-accent rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Families Section */}
      <section className="section-padding bg-white relative" data-animate>
        {/* Creative quote element — purely decorative, faded to 10% by design and
            hidden from assistive tech via aria-hidden. */}
        <div aria-hidden="true" className="absolute top-20 left-10 opacity-10 pointer-events-none">
          <div className="text-9xl font-display text-brand-accent">"</div>
        </div>

        <div className="content-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

            {/* Left: Visual Elements */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-8 h-[600px]">
                <div className="space-y-8">
                  <div className="bg-blue-50 h-48 p-8 flex flex-col justify-end border-l-4 border-brand-navy">
                    <h3 className="font-display text-heading mb-2">Cultural Inclusion</h3>
                    <p className="text-sm text-muted">Race & culture valued</p>
                    <div className="mt-4 w-8 h-8 bg-brand-navy/20 rounded-full flex items-center justify-center">
                      <PencilIcon className="w-4 h-4 text-brand-navy" />
                    </div>
                  </div>
                  <div className="bg-brand-navy/5 h-32 p-6 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="font-display text-heading text-2xl mb-1">96%</h3>
                      <p className="text-xs text-muted uppercase tracking-wide">Sense of Belonging</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-8 pt-16">
                  <div className="bg-brand-yellow/5 h-32 p-6 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="font-display text-heading text-2xl mb-1">96%</h3>
                      <p className="text-xs text-muted uppercase tracking-wide">Parent-Teacher Trust</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 h-48 p-8 flex flex-col justify-end border-l-4 border-brand-yellow">
                    <h3 className="font-display text-heading mb-2">Parent Partnership</h3>
                    <p className="text-sm text-muted">Respected & supported</p>
                    <div className="mt-4 w-8 h-8 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                      <AcademicCapIcon className="w-4 h-4 text-brand-primary" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating accent element */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white shadow-lg rounded-full flex items-center justify-center z-10">
                <div className="w-8 h-8 bg-brand-accent rounded-full"></div>
              </div>
            </div>

            {/* Right: Content */}
            <div>
              <span className="text-brand-accent-dark font-medium mb-8 block tracking-wide">Family Voices</span>

              <h2 className="text-display text-heading font-display mb-8 leading-tight">
                What Are Families Saying?
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-lg text-body">
                  Our families are at the heart of our community. We foster strong partnerships with parents through open communication,
                  mutual respect, and a shared commitment to student success. Our inclusive environment values each child's unique background
                  and prepares them for a bright future.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-12">
                <div className="text-center p-8 bg-brand-navy/5 border border-brand-navy/20">
                  <div className="text-4xl font-display text-brand-navy mb-2">98%</div>
                  <div className="text-sm text-muted uppercase tracking-wide">Race & Culture Valued</div>
                  <div className="w-12 h-px bg-brand-navy mx-auto mt-4"></div>
                </div>
                <div className="text-center p-8 bg-brand-yellow/5 border border-brand-yellow/20">
                  <div className="text-4xl font-display text-brand-primary mb-2">97%</div>
                  <div className="text-sm text-muted uppercase tracking-wide">Educational Satisfaction</div>
                  <div className="w-12 h-px bg-brand-yellow mx-auto mt-4"></div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-body">
                  <strong>We have open enrollment.</strong> Schedule a visit today to learn more about our community of authors.
                </p>
                <a href="/contact" className="inline-flex items-center btn-primary">Schedule a Visit</a>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Call to Action */}
      <section className="relative section-padding band-warm text-brand-text-primary overflow-hidden" data-animate>
        {/* Background pattern */}
        <div aria-hidden="true" className="opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border-2 border-brand-primary/20 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border-2 border-brand-primary/20 rounded-full"></div>
          <div className="absolute top-1/2 right-10 w-16 h-16 border-2 border-brand-accent/30 rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-16">
            <div className="inline-block p-4 bg-white shadow-sm rounded-full mb-8">
              <div className="w-8 h-8 bg-brand-accent rounded-full"></div>
            </div>
            <h2 className="text-display font-display mb-8 text-brand-text-primary">Ready to Begin Your Story?</h2>
            <p className="text-xl leading-relaxed font-body text-body max-w-3xl mx-auto">
              Join our community of young authors and discover your unique voice in our nurturing, creative environment where imagination becomes reality.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button className="bg-brand-primary text-white font-medium px-10 py-4 hover:bg-brand-navy transition-all duration-300 shadow-lg hover:shadow-xl">
              Schedule a Tour
            </button>
            <button className="border-2 border-brand-primary/40 text-brand-text-primary font-medium px-10 py-4 hover:bg-white transition-all duration-300">
              Apply for Admission
            </button>
          </div>

          {/* Additional info */}
              <div className="mt-12 pt-12 border-t border-brand-primary/20">
                <p className="text-sm text-body font-medium">
                  Open enrollment available • Contact us at (212) 860-5803 to schedule a visit
                </p>
              </div>
        </div>
      </section>

      {/* Image Enlargement Modal */}
      {enlargedImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged image view"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full">
            {/* Close button */}
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-brand-accent transition-colors p-2 rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Close enlarged image"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image */}
            <img
              src={enlargedImage.src}
              alt={enlargedImage.alt}
              lang={enlargedImage.lang}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <p lang={enlargedImage.lang} className="text-white text-center font-medium">{enlargedImage.alt}</p>
            </div>
          </div>
        </div>
      )}

      </main>
      <Footer />
    </>
  );
};

export default Home;